<template>
  <Scatter :data="chartData" :options="chartOptions" :plugins="[quadrantPlugin]" />
</template>

<script>
import { Scatter } from 'vue-chartjs'
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js'

ChartJS.register(LinearScale, PointElement, Tooltip)

// Draws the 4.0 threshold lines and quadrant labels directly on the canvas.
// Registered per-chart via :plugins prop so it doesn't affect other charts.
const quadrantPlugin = {
  id: 'attachmentQuadrant',
  afterDraw(chart) {
    const { ctx, chartArea: { left, right, top, bottom }, scales: { x, y } } = chart

    const cx = x.getPixelForValue(4)  // vertical threshold (Avoidance = 4)
    const cy = y.getPixelForValue(4)  // horizontal threshold (Anxiety = 4)

    ctx.save()

    // Dashed threshold lines
    ctx.strokeStyle = 'rgba(0,0,0,0.18)'
    ctx.lineWidth   = 1
    ctx.setLineDash([5, 5])

    ctx.beginPath(); ctx.moveTo(cx, top);  ctx.lineTo(cx, bottom); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(left, cy); ctx.lineTo(right, cy);  ctx.stroke()

    ctx.setLineDash([])

    // Quadrant labels
    ctx.font         = '11px sans-serif'
    ctx.fillStyle    = 'rgba(0,0,0,0.45)'
    ctx.textAlign    = 'center'
    ctx.textBaseline = 'middle'

    const midLeft  = (left + cx) / 2
    const midRight = (cx + right) / 2
    const midTop   = (top + cy) / 2
    const midBot   = (cy + bottom) / 2

    ctx.fillText('Securizant',    midLeft,  midBot)   // low anxiety, low avoidance
    ctx.fillText('Anxios',        midLeft,  midTop)   // high anxiety, low avoidance
    ctx.fillText('Detașat',       midRight, midBot)   // low anxiety, high avoidance
    ctx.fillText('Evitant anxios',midRight, midTop)   // high anxiety, high avoidance

    ctx.restore()
  },
}

export default {
  name: 'AttachmentChart',
  components: { Scatter },

  props: {
    /**
     * ECR-12 Anxiety subscale score.
     * Shape: { score: number, norm?: number }
     *   score — mean of anxiety items, 1.0–7.0 (higher = more anxious attachment)
     *   norm  — optional normalised value, 0.0–1.0
     */
    anxiety: {
      type: Object,
      required: true,
      validator: v => typeof v?.score === 'number',
    },

    /**
     * ECR-12 Avoidance subscale score.
     * Shape: { score: number, norm?: number }
     *   score — mean of avoidance items, 1.0–7.0 (higher = more avoidant attachment)
     *   norm  — optional normalised value, 0.0–1.0
     */
    avoidance: {
      type: Object,
      required: true,
      validator: v => typeof v?.score === 'number',
    },
  },

  data() {
    return { quadrantPlugin }
  },

  computed: {
    chartData() {
      return {
        datasets: [{
          data: [{ x: this.avoidance.score, y: this.anxiety.score }],
          backgroundColor: 'rgba(25, 118, 210, 0.85)',
          pointRadius:      9,
          pointHoverRadius: 11,
          clip:             false,
        }],
      }
    },

    chartOptions() {
      return {
        responsive:          true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: () => `Anxietate: ${this.anxiety.score.toFixed(2)}  ·  Evitare: ${this.avoidance.score.toFixed(2)}`,
            },
          },
        },
        scales: {
          x: {
            title: { display: true, text: 'Evitare (Avoidance)', font: { size: 13 }, color: 'rgba(0,0,0,0.65)' },
            min: 1, max: 7,
            ticks: { stepSize: 1 },
            grid:   { color: ctx => ctx.tick.value === 4 ? 'transparent' : 'rgba(0,0,0,0.06)' },
            border: { color: 'rgba(0,0,0,0.45)', width: 2 },
          },
          y: {
            title: { display: true, text: 'Anxietate', font: { size: 13 }, color: 'rgba(0,0,0,0.65)' },
            min: 1, max: 7,
            ticks: { stepSize: 1 },
            grid:   { color: ctx => ctx.tick.value === 4 ? 'transparent' : 'rgba(0,0,0,0.06)' },
            border: { color: 'rgba(0,0,0,0.45)', width: 2 },
          },
        },
      }
    },
  },
}
</script>
